import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ROLE_LABELS } from '@shared/roles';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { overlay } from '../overlay';
import { useTheme } from '../hooks/useTheme';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { NAV_ITEMS, currentSectionLabel } from './nav';
import { useAuthStore } from './authStore';
import { CrossSiteNotice, useCrossSiteInfo } from '../components/CrossSiteNotice';

function NavIcon({ path }: { path: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="size-4 shrink-0">
      <path d={path} fill="currentColor" />
    </svg>
  );
}

function ThemeToggle() {
  const { mode, setMode } = useTheme();
  const label = mode === 'light' ? '浅色' : mode === 'dark' ? '深色' : '跟随系统';
  const next = mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light';
  const nextLabel = next === 'light' ? '浅色' : next === 'dark' ? '深色' : '跟随系统';
  return (
    <Button size="sm" variant="ghost" title={`当前：${label}（点击切换为${nextLabel}）`} onClick={() => setMode(next)} aria-label={`主题：${label}`}>
      {mode === 'light' ? '☀' : mode === 'dark' ? '☾' : '◐'}
      <span className="hidden sm:inline">{label}</span>
    </Button>
  );
}

function UserMenu() {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);

  if (!user) return null;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-md border border-line bg-surface-1 px-2 py-1.5 text-left hover:bg-surface-2"
      >
        <span className="flex size-6 items-center justify-center rounded-full bg-accent-soft text-[11px] font-semibold text-accent">
          {user.name.slice(0, 1).toUpperCase()}
        </span>
        <span className="hidden flex-col leading-tight sm:flex">
          <span className="text-xs font-medium text-fg">{user.name}</span>
          <span className="text-[10px] text-fg-muted">{ROLE_LABELS[user.role]}</span>
        </span>
        <span aria-hidden className="text-[10px] text-fg-subtle">
          ▾
        </span>
      </button>

      {open ? (
        <div role="menu" className="absolute right-0 z-40 mt-1.5 w-60 overflow-hidden rounded-lg border border-line-strong bg-surface-1">
          <div className="border-b border-line px-3 py-2.5">
            <p className="truncate text-sm font-medium text-fg">{user.name}</p>
            <p className="truncate text-xs text-fg-muted">{user.email}</p>
            <div className="mt-1.5">
              <Badge tone="accent">{ROLE_LABELS[user.role]}</Badge>
            </div>
          </div>
          <div className="flex flex-col p-1">
            <button
              type="button"
              role="menuitem"
              className="rounded px-2.5 py-2 text-left text-sm text-fg-muted hover:bg-surface-2 hover:text-fg"
              onClick={() => {
                setOpen(false);
                navigate('/account/password');
              }}
            >
              修改口令
            </button>
            <button
              type="button"
              role="menuitem"
              className="rounded px-2.5 py-2 text-left text-sm text-critical hover:bg-critical-soft"
              onClick={async (event) => {
                // 菜单马上会被卸载，先把按钮位置存下来，弹窗才能照旧从用户头像旁飞出
                const anchorRect = event.currentTarget.getBoundingClientRect();
                setOpen(false);
                // 退出登录有副作用，统一走浮层确认，不使用浏览器原生 confirm
                const okToLogout = await overlay.confirm({
                  placementKey: 'nav.logout',
                  trigger: event.currentTarget,
                  anchorRect,
                  title: '退出登录？',
                  description: '退出后需要重新输入邮箱与口令才能进入平台。',
                  confirmText: '退出登录',
                  tone: 'danger',
                });
                if (!okToLogout) return;
                await logout();
                navigate('/login', { replace: true });
              }}
            >
              退出登录
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * 应用外壳：左侧导航 + 顶部工具条 + 内容区。
 *
 * 窄屏把导航收进抽屉。抽屉是导航容器而不是对话框，因此自带背景遮罩，
 * 不走统一浮层系统。
 */
export function AppShell() {
  const permissions = useAuthStore((state) => state.permissions);
  const { isDesktop } = useBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const { mismatched: crossSite } = useCrossSiteInfo();

  const items = NAV_ITEMS.filter((item) => permissions.includes(item.permission));

  /*
   * 首页有一整套自己的配色（见 styles/index.css 的 .theme-home）。
   * 挂在 <main> 上而不是包一层 div：main 是 min-h-dvh 的弹性列里
   * flex-1 的那一格，背景因此天然铺满视口，不需要给容器算高度。
   * 侧边栏与顶栏在 main 之外，仍然用全局配色 —— 换肤只发生在内容区。
   */
  const isHome = location.pathname === '/';

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const nav = (
    <nav className="flex flex-col gap-0.5 p-2">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          title={item.description}
          className={({ isActive }) =>
            [
              'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors',
              isActive ? 'bg-accent-soft font-medium text-accent' : 'text-fg-muted hover:bg-surface-2 hover:text-fg',
            ].join(' ')
          }
        >
          <NavIcon path={item.icon} />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-dvh bg-surface-0 text-fg">
      {isDesktop ? (
        <aside className="flex w-60 shrink-0 flex-col border-r border-line bg-surface-1">
          <div className="flex h-14 items-center gap-2 border-b border-line px-4">
            <span className="flex size-7 items-center justify-center rounded-md bg-accent text-sm font-bold text-accent-fg">H</span>
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-semibold">员工平台</span>
              <span className="text-[10px] text-fg-subtle">Havorix Aero</span>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">{nav}</div>
          <div className="border-t border-line px-3 py-2 text-[10px] leading-relaxed text-fg-subtle">
            提出创意 → AI 快速研判 → 协作推进
            <br />
            研判结论由模型生成，决定权在人
          </div>
        </aside>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-line bg-surface-1/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-surface-1/80">
          {!isDesktop ? (
            <>
              <Button size="sm" variant="ghost" aria-label="打开导航" aria-expanded={drawerOpen} onClick={() => setDrawerOpen(true)}>
                ☰
              </Button>
              <span className="flex size-7 items-center justify-center rounded-md bg-accent text-sm font-bold text-accent-fg">H</span>
            </>
          ) : null}
          <div className="min-w-0 flex-1 truncate text-sm font-medium text-fg-muted">
            {currentSectionLabel(items, location.pathname)}
          </div>
          <ThemeToggle />
          <UserMenu />
        </header>

        {crossSite ? (
          <div className="border-b border-caution/40 px-4 py-2">
            <CrossSiteNotice />
          </div>
        ) : null}

        <main className={['min-w-0 flex-1', isHome ? 'theme-home theme-home-bg' : ''].join(' ')}>
          <Outlet />
        </main>
      </div>
      {!isDesktop && drawerOpen ? (
        <div className="fixed inset-0 z-50">
          <div aria-hidden className="absolute inset-0 bg-black/45 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <div className="relative h-full w-64 max-w-[80vw] border-r border-line bg-surface-1">
            <div className="flex h-14 items-center justify-between border-b border-line px-3">
              <span className="text-sm font-semibold">导航</span>
              <Button size="sm" variant="ghost" aria-label="关闭导航" onClick={() => setDrawerOpen(false)}>
                ✕
              </Button>
            </div>
            {nav}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** 页面级容器：统一内边距与最大宽度。 */
export function PageContainer({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={['mx-auto flex w-full max-w-[1600px] flex-col gap-4 p-4', className].join(' ')}>{children}</div>;
}

/** 页面标题区：标题 + 说明 + 右侧动作。 */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-lg font-semibold text-fg">{title}</h1>
        {description ? <p className="mt-0.5 max-w-3xl text-xs text-fg-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}
